export const removeFromOrder = (order, setOrder, selectedOrderIndex, setSelectedOrderIndex) => {
  if (selectedOrderIndex === null) return;

  const updatedOrder = order.filter((_, index) => index !== selectedOrderIndex);
  setOrder(updatedOrder);
  setSelectedOrderIndex(null);
};

export const updateQuantity = (order, setOrder, selectedOrderIndex, quantity) => {
  if (selectedOrderIndex === null) return;

  const updatedOrder = order.map((item, index) => {
    if (index === selectedOrderIndex) {
      return { ...item, quantity };
    }
    return item;
  });

  setOrder(updatedOrder);
};

export const confirmOrder = async (
  chequeSeleccionado,
  order,
  appliedPayments,
  selectedRevenue,
  setOrder,
  setAppliedPayments,
  setSelectedOrderIndex,
  setCurrentGroup,
  setCurrentCategory,
  setChequeSeleccionado,
  mostrarMensaje,
  handleChangeRevenue,
  API_PAGO,
  calculateTotal
) => {
  try {
    if (!chequeSeleccionado) {
      throw new Error("No hay un cheque seleccionado.");
    }

    const numeroCheque = Number(chequeSeleccionado);
    if (isNaN(numeroCheque)) {
      throw new Error("El número de cheque no es válido.");
    }

    // 1. VERIFICAR PAGOS DE HOTEL ANTES DE CONTINUAR
    const hotelPayments = appliedPayments.filter(p => p.isHotelPayment);
    
    for (const hotelPayment of hotelPayments) {
      if (!hotelPayment.hotelData || !hotelPayment.hotelData.success) {
        throw new Error(
          `❌ El pago ${hotelPayment.method.name} ` +
          `(Hab. ${hotelPayment.roomNumber || hotelPayment.method?.PM || "N/A"}) ` +
          `no fue procesado en el hotel. Error: ${hotelPayment.hotelData?.message || "Sin datos"}`
        );
      }
      
      if (!hotelPayment.roomNumber && !hotelPayment.method?.PM) {
        throw new Error(
          `❌ El pago ${hotelPayment.method.name} no tiene número de habitación.`
        );
      }
    }

    const total = calculateTotal(order);
    const totalPagado = appliedPayments.reduce((sum, p) => sum + p.amount, 0);
    const montoPagado = Number(totalPagado);
    
    if (isNaN(montoPagado)) {
      throw new Error("El monto pagado no es válido.");
    }

    if (montoPagado < total) {
      throw new Error("El pago no está completo.");
    }

    // 2. INCLUIR DATOS DE HOTEL EN LOS PAGOS
    const requestData = {
      numero_cheque: numeroCheque,
      monto_pagado: montoPagado,
      total_pedido: total,
      pagos: appliedPayments.map((pago) => {
        const basePago = {
          id_pago: pago.method.id || 0,
          nombre_pago: pago.method.name || "Método desconocido",
          monto_pagado: parseFloat(pago.amount.toFixed(2)),
          propina: parseFloat((pago.tip || 0).toFixed(2)),
          cambio: parseFloat((pago.change || 0).toFixed(2)),
          folio: pago.folio || "",
        };
        
        // Añadir datos de hotel si es pago de hotel
        if (pago.isHotelPayment) {
          return {
            ...basePago,
            es_pago_hotel: true,
            numero_habitacion: pago.roomNumber || pago.method?.PM || "",
            referencia_hotel: pago.hotelData?.referencia || pago.hotelData?.bridge_url || "",
            respuesta_hotel: JSON.stringify(pago.hotelData || {}),
            ...(pago.method?.PM && { pm_predefinido: pago.method.PM })
          };
        }
        
        return basePago;
      }),
      ...(hotelPayments.length > 0 && { contiene_pagos_hotel: true })
    };

    console.log("Datos enviados:", requestData);

    const response = await fetch(API_PAGO, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Error al confirmar el pago.");
    }

    const data = await response.json();
    console.log("Respuesta del servidor:", data);

    if (data.success) {
      setOrder([]);
      setAppliedPayments([]);
      setSelectedOrderIndex(null);
      setCurrentGroup(null);
      setCurrentCategory(null);
      setChequeSeleccionado(null);
      
      // Mostrar resumen de pagos de hotel
      if (hotelPayments.length > 0) {
        const resumenHotel = hotelPayments.map(p => 
          `${p.method.name}: $${p.amount} (Hab. ${p.roomNumber || p.method?.PM})`
        ).join(', ');
        mostrarMensaje(`✅ Pago confirmado! Cheque #${numeroCheque} cerrado. Pagos hotel: ${resumenHotel}`);
      } else {
        mostrarMensaje("Pago confirmado y cheque cerrado correctamente.");
      }

      if (selectedRevenue.Tipo !== "Retail") {
        handleChangeRevenue();
      }
    } else {
      throw new Error(data.message || "Error al confirmar el pago.");
    }
  } catch (error) {
    console.error("Error al confirmar el pago:", error);
    mostrarMensaje(error.message || "Hubo un error al confirmar el pago. Inténtalo de nuevo.");
  }
};

// FUNCIÓN ACTUALIZADA CON MANEJO DE PAGOS DE HOTEL
export const handlePaymentConfirm = (setAppliedPayments, payment) => {
  // Añadir datos de validación para pagos de hotel
  const validatedPayment = {
    ...payment,
    // Asegurar que isHotelPayment sea booleano
    isHotelPayment: payment.isHotelPayment || false,
    // Si es pago de hotel, validar datos mínimos
    ...(payment.isHotelPayment && {
      hotelData: payment.hotelData || { success: false, message: "Sin datos de hotel" },
      roomNumber: payment.roomNumber || payment.method?.PM || "",
      fechaHotel: new Date().toISOString()
    })
  };

  setAppliedPayments((prevPayments) => {
    // Verificar si ya existe EL MISMO pago (misma transacción), no solo el mismo
    // método+monto. El monto puede repetirse legítimamente entre pagos distintos
    // (varias tarjetas, varios comensales pagando lo mismo, etc.), así que ya no
    // se usa como llave de deduplicación. El folio (referencia/autorización) es
    // el único dato que identifica de forma confiable una transacción concreta;
    // si el pago no trae folio, siempre se agrega como una nueva entrada.
    const folioValidado = (validatedPayment.folio || "").trim();
    const tieneFolio = folioValidado !== "";

    const existingIndex = tieneFolio
      ? prevPayments.findIndex(p =>
          p.method.id === validatedPayment.method.id &&
          (p.folio || "").trim() === folioValidado &&
          p.isHotelPayment === validatedPayment.isHotelPayment &&
          (validatedPayment.isHotelPayment
            ? p.roomNumber === validatedPayment.roomNumber
            : true)
        )
      : -1;

    if (existingIndex !== -1) {
      // Actualizar pago existente (mismo método + mismo folio = misma transacción)
      const updated = [...prevPayments];
      updated[existingIndex] = validatedPayment;
      return updated;
    }

    // Añadir nuevo pago (incluye método+monto repetidos sin folio)
    return [...prevPayments, validatedPayment];
  });
};

// FUNCIÓN ACTUALIZADA CON ADVERTENCIA PARA PAGOS DE HOTEL
export const handleRemovePayment = (appliedPayments, setAppliedPayments, index, setSelectedPaymentIndex) => {
  if (index < 0 || index >= appliedPayments.length) return;
  
  const removedPayment = appliedPayments[index];
  
  // Mostrar advertencia si es pago de hotel
  if (removedPayment.isHotelPayment) {
    const confirm = window.confirm(
      `⚠️ Este pago ya fue procesado en el hotel (Hab. ${removedPayment.roomNumber || removedPayment.method?.PM}).\n` +
      `¿Estás seguro de que quieres eliminarlo?`
    );
    
    if (!confirm) return;
  }
  
  const updatedPayments = appliedPayments.filter((_, i) => i !== index);
  setAppliedPayments(updatedPayments);
  setSelectedPaymentIndex(null);
};

export const generateOrderSummaryTxt = (order, total, appliedPayments) => {
  // Lógica para generar el resumen del pedido en formato TXT
  // Puedes añadir info de pagos de hotel aquí si lo necesitas
  
  let txt = `=== RESUMEN DE PEDIDO ===\n`;
  txt += `Total: $${total.toFixed(2)}\n\n`;
  
  // Pagos aplicados
  txt += `=== PAGOS APLICADOS ===\n`;
  appliedPayments.forEach((pago, index) => {
    txt += `${index + 1}. ${pago.method.name}: $${pago.amount.toFixed(2)}\n`;
    if (pago.isHotelPayment) {
      txt += `   Habitación: ${pago.roomNumber || pago.method?.PM}\n`;
    }
    if (pago.tip > 0) txt += `   Propina: $${pago.tip.toFixed(2)}\n`;
    if (pago.folio) txt += `   Folio: ${pago.folio}\n`;
  });
  
  return txt;
};